import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Copy } from 'lucide-react';
import type { BulkCreateUsersResponse } from '@/lib/types/mise/user';

interface BulkCreateUserResultStepProps {
  createResult: BulkCreateUsersResponse;
  copiedRowIndex: number | null;
  onCopyPassword: (rowIndex: number, password: string) => void;
}

export function BulkCreateUserResultStep({
  createResult,
  copiedRowIndex,
  onCopyPassword,
}: BulkCreateUserResultStepProps) {
  const hasTemporaryPassword = createResult.results.some(
    (r) => r.status === 'success' && !!r.temporary_password,
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <Typography>
        {createResult.summary.total}件中、{createResult.summary.success}件成功、
        {createResult.summary.failed}件失敗
      </Typography>

      {hasTemporaryPassword && (
        <Alert severity="warning">
          仮パスワードはダイアログを閉じると再表示できません。CSVをダウンロードしてください
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="一括登録結果テーブル">
          <TableHead>
            <TableRow>
              <TableCell>行番号</TableCell>
              <TableCell>メールアドレス</TableCell>
              <TableCell>ユーザー名</TableCell>
              <TableCell>店舗ID</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>仮パスワード</TableCell>
              <TableCell>エラー</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {createResult.results.map((row) => (
              <TableRow key={row.row_index}>
                <TableCell>{row.row_index + 1}</TableCell>
                <TableCell>{row.user_email}</TableCell>
                <TableCell>{row.user_name}</TableCell>
                <TableCell>{row.seller_id}</TableCell>
                <TableCell>
                  {row.status === 'success' ? (
                    <Chip label="成功" color="success" size="small" />
                  ) : (
                    <Chip label="失敗" color="error" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  {row.temporary_password ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <code
                        style={{
                          padding: '2px 6px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px',
                          fontFamily: 'monospace',
                          wordBreak: 'break-all',
                        }}
                      >
                        {row.temporary_password}
                      </code>
                      <IconButton
                        size="small"
                        title="コピー"
                        onClick={() => onCopyPassword(row.row_index, row.temporary_password ?? '')}
                      >
                        <Copy size={14} />
                      </IconButton>
                      {copiedRowIndex === row.row_index && (
                        <Typography variant="caption" color="success.main">
                          コピー済み
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    ''
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{row.error ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
